import React, { useEffect, useState } from 'react';
import { Info, Loader2, Package, Plus, Save, Trash2, ChevronDown, Copy } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { moduleApi, apiFetch } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { AmbianceBand } from '../Ambiance/AmbianceBand';
import { useAmbiance } from '../Ambiance/AmbianceContext';
import { AMB } from '../../lib/ambiances';
import { Field, Input, Segmented } from '../ui/Field';
import { useUndo } from '../ui/UndoBar';
import { SimulateurParcoursSettings, type PrestationSubcategory } from './SimulateurObjets';
import { PrestationForm } from './PrestationForm';
import { AddPrestationModal } from './AddPrestationModal';
import { CategoryManager } from './CategoryManager';
import { FormulesSection } from './FormulesSection';
import { FormulesProPage } from './FormulesProPage';
import { TarifsProSection } from './TarifsProSection';
import {
    type PrestationCatalogItem,
    type PrestationCategory,
    type ClientTypeScope,
    type UnitType,
    UNIT_LABEL_COURT,
    emptyItem,
    lireOverrides,
    ecrireOverrides,
} from './prestationsTypes';

export const PrestationsModule: React.FC = () => {
    const [items, setItems] = useState<PrestationCatalogItem[]>([]);
    const [categories, setCategories] = useState<PrestationCategory[]>([]);
    const [allowedTvaRates, setAllowedTvaRates] = useState<number[]>([20]);
    const [fullSettings, setFullSettings] = useState<any>(null);
    const { user } = useAuth();
    const ambiance = useAmbiance();
    const undo = useUndo();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [activeSection, setActiveSection] = useState<'catalogue' | 'formules' | 'tarifs'>('catalogue');
    // Options dont le prix est un pourcentage réglé dans « Réductions et options » (le prix du catalogue ne sert alors plus) ; relu à chaque retour sur le catalogue.
    const [optionsEnPourcent, setOptionsEnPourcent] = useState<Set<string>>(new Set());
    useEffect(() => {
        if (activeSection !== 'catalogue') return;
        let annule = false;
        Promise.all(['PROFESSIONNEL', 'PRIVÉ'].map((a) => apiFetch(`/prestation-simulateur/tarifs-pro?audience=${encodeURIComponent(a)}`).catch(() => null)))
            .then((reponses: any[]) => {
                if (annule) return;
                const ids = new Set<string>();
                for (const r of reponses) for (const o of (r?.config?.options || [])) if (Object.values(o?.taux || {}).some((t) => Number(t) > 0)) ids.add(String(o.item_id));
                setOptionsEnPourcent(ids);
            });
        return () => { annule = true; };
    }, [activeSection]);
    // Onglets dédiés Particulier (2026-09-03, Emeni) : le catalogue et les formules configurés jusqu'ici
    // étaient de facto tous "pour les professionnels" — deux onglets séparés permettent de constituer une
    // liste et des formules propres aux particuliers, sans mélanger les deux catalogues à l'écran.
    const [activeAudience, setActiveAudience] = useState<ClientTypeScope>('PROFESSIONNEL');
    const visibleItems = items.filter((it) => (it.client_type_scope || 'TOUS') === 'TOUS' || it.client_type_scope === activeAudience);

    const [subcategories, setSubcategories] = useState<PrestationSubcategory[]>([]);
    const loadSubcategories = () => {
        moduleApi.getPrestationSubcategories().then((r: any) => setSubcategories(Array.isArray(r) ? r : [])).catch(() => setSubcategories([]));
    };
    useEffect(() => { loadSubcategories(); }, []);

    const loadCategories = () => {
        moduleApi.getPrestationCategories().then((r: any) => setCategories(Array.isArray(r) ? r : [])).catch(() => setCategories([]));
    };
    useEffect(() => { loadCategories(); }, []);

    useEffect(() => {
        (async () => {
            try {
                const data = await moduleApi.getBillingSettings();
                setFullSettings(data || {});
                const rates = Array.isArray(data?.tva_rates) && data.tva_rates.length > 0 ? data.tva_rates.map(Number) : [20];
                setAllowedTvaRates(rates);
                let parsed: any[] = [];
                try {
                    parsed = JSON.parse(data?.prestations_catalog || '[]');
                } catch {
                    parsed = [];
                }
                setItems(
                    (Array.isArray(parsed) ? parsed : [])
                        .map((p: any, index: number) => ({
                            id: String(p?.id || index + 1),
                            label: String(p?.label || '').trim(),
                            unit_price_ttc: Number(p?.unit_price_ttc ?? 0),
                            tax_rate: Number(p?.tax_rate ?? rates[0] ?? 20),
                            show_tva: p?.show_tva !== false,
                            unit_type: (['unite', 'm2', 'ml', 'surface'].includes(p?.unit_type) ? p.unit_type : 'unite') as UnitType,
                            surface_width: Number(p?.surface_width ?? 0),
                            surface_length: Number(p?.surface_length ?? 0),
                            // 0 quand rien n'est enregistré, jamais 30 : c'est 0 que le reste du système
                            // utilise réellement (durée d'un rendez-vous = quantité × ce temps ÷ salariés),
                            // et afficher 30 ici laissait croire que la durée était configurée — puis
                            // l'écrivait pour de bon au premier enregistrement.
                            default_duration_minutes: Number(p?.default_duration_minutes ?? 0),
                            category_id: String(p?.category_id || ''),
                            // Catalogue existant jamais scopé auparavant : traité comme "Professionnels" au
                            // chargement (Emeni : "j'ai configuré ce qui est pour les professionnels ici"),
                            // jamais 'TOUS' par défaut qui l'aurait fait apparaître aussi côté Particulier.
                            client_type_scope: (['TOUS', 'PROFESSIONNEL', 'PRIVÉ'].includes(p?.client_type_scope) ? p.client_type_scope : 'PROFESSIONNEL') as ClientTypeScope,
                            // Champs du parcours guidé : absents du catalogue existant, donc valeurs neutres
                            // — une ligne déjà en place reste une prestation visible, rattachée à rien.
                            kind: (p?.kind === 'option' ? 'option' : 'prestation') as 'prestation' | 'option',
                            subcategory_id: String(p?.subcategory_id || ''),
                            option_item_ids: Array.isArray(p?.option_item_ids) ? p.option_item_ids.map(String) : [],
                            option_overrides: lireOverrides(p?.option_overrides),
                            simulateur_label: String(p?.simulateur_label || ''),
                            simulateur_hidden: p?.simulateur_hidden === true,
                            // Où afficher la photo de la prestation — la photo elle-même est chargée à part.
                            image_show_demarche: p?.image_show_demarche === true,
                            image_show_simulateur: p?.image_show_simulateur === true,
                            // Lieu propre à la prestation : absent d'un catalogue existant, donc vide —
                            // la prestation suit alors le réglage de l'établissement, comme avant.
                            location_mode: (['domicile', 'entreprise', 'les_deux'].includes(p?.location_mode) ? p.location_mode : '') as '' | 'domicile' | 'entreprise' | 'les_deux',
                        }))
                        .filter((item: PrestationCatalogItem) => item.label.length > 0)
                );
            } catch (e) {
                console.error('Failed to load prestations catalog', e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // Le catalogue ne part en base qu'au bouton « Enregistrer », alors que les reponses du parcours,
    // elles, s'enregistrent a chaque frappe. Rattacher une prestation a un objet puis regarder l'apercu
    // donnait donc l'impression que rien ne se passait — on suit donc l'etat « pas encore enregistre »
    // pour pouvoir le dire (2026-09-09).
    const [catalogueModifie, setCatalogueModifie] = useState(false);

    const updateItem = (id: string, patch: Partial<PrestationCatalogItem>) => {
        setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
        setCatalogueModifie(true);
    };

    // Suppression immédiate + bandeau « Annuler » de 10 s (passation §6) : demander confirmation à
    // chaque ligne fatigue et finit par être cliqué sans lire. La ligne est remise à sa place exacte
    // si on annule — pas rejetée en fin de liste.
    const removeItem = (id: string) => {
        const index = items.findIndex((item) => item.id === id);
        const removed = items[index];
        if (!removed) return;
        setItems((prev) => prev.filter((item) => item.id !== id));
        setCatalogueModifie(true);
        if (expandedId === id) setExpandedId(null);
        undo.push({
            message: `« ${removed.label || 'Sans nom'} » supprimée du catalogue`,
            undoneMessage: 'Prestation rétablie',
            undo: () => setItems((prev) => [...prev.slice(0, index), removed, ...prev.slice(index)]),
        });
    };

    // Dupliquer (2026-09-05, Emeni) : une prestation n'a qu'une seule catégorie (cf. category_id
    // plus haut) — dupliquer permet de réutiliser le même tarif/durée pour une variante rangée
    // sous une autre catégorie, sans ressaisir tout le formulaire.
    const duplicateItem = (id: string) => {
        const source = items.find((item) => item.id === id);
        if (!source) return;
        const copy: PrestationCatalogItem = { ...source, id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, label: `${source.label} (copie)` };
        setItems((prev) => {
            const index = prev.findIndex((item) => item.id === id);
            return [...prev.slice(0, index + 1), copy, ...prev.slice(index + 1)];
        });
        setExpandedId(copy.id);
    };

    const handleCreate = (item: PrestationCatalogItem) => {
        setItems((prev) => [...prev, item]);
        setCatalogueModifie(true);
        setShowAddModal(false);
        setExpandedId(item.id);
    };

    const handleSave = async () => {
        if (!fullSettings) return;
        setSaving(true);
        try {
            const prestations_catalog = JSON.stringify(
                items
                    // L'identifiant ne doit JAMAIS être renuméroté sur la position (2026-09-06) : les formules
                    // et les lignes de devis désignent une prestation par cet identifiant. Ajouter, supprimer
                    // ou dupliquer une ligne décalait tout le catalogue d'un cran, et la réduction prévue pour
                    // « Chaise Osier » se retrouvait appliquée au « Fauteuil ». Les nouvelles lignes reçoivent
                    // déjà un identifiant unique à la création (emptyItem / duplicateItem).
                    .map((item) => {
                        // Une option ne vise que des prestations du même public (Pro / Particulier / Tous) : on retire à
                        // l'enregistrement les cibles d'un autre public, restes d'anciens réglages (ex. une chaise Particulier
                        // sur une option Pro), qui apparaissaient en double dans la liste.
                        const visees = item.kind === 'option'
                            ? (item.option_item_ids || []).filter((idCible) => {
                                const cible = items.find((x) => x.id === idCible);
                                if (!cible) return false;
                                const sOption = item.client_type_scope || 'TOUS';
                                const sCible = cible.client_type_scope || 'TOUS';
                                return sOption === 'TOUS' || sCible === 'TOUS' || sOption === sCible;
                            })
                            : [];
                        return {
                        id: item.id,
                        label: item.label.trim(),
                        unit_price_ttc: Number(item.unit_price_ttc ?? 0),
                        tax_rate: item.show_tva ? Number(item.tax_rate ?? allowedTvaRates[0] ?? 20) : 0,
                        show_tva: item.show_tva,
                        unit_type: item.unit_type,
                        surface_width: Number(item.surface_width ?? 0),
                        surface_length: Number(item.surface_length ?? 0),
                        default_duration_minutes: Number(item.default_duration_minutes ?? 0),
                        category_id: item.category_id || '',
                        client_type_scope: item.client_type_scope || 'TOUS',
                        kind: item.kind === 'option' ? 'option' : 'prestation',
                        // Une option n'appartient à aucun objet, une prestation ne vise personne : on
                        // n'enregistre pas des restes de l'autre nature après une bascule.
                        subcategory_id: item.kind === 'option' ? '' : (item.subcategory_id || ''),
                        option_item_ids: visees,
                        // Seules les surcharges de prestations encore visées sont gardées.
                        option_overrides: item.kind === 'option' ? ecrireOverrides(item.option_overrides, visees) : {},
                        simulateur_label: (item.simulateur_label || '').trim(),
                        simulateur_hidden: item.simulateur_hidden === true,
                        image_show_demarche: item.image_show_demarche === true,
                        image_show_simulateur: item.image_show_simulateur === true,
                        };
                    })
                    .filter((item) => item.label.length > 0)
            );
            // On renvoie l'objet complet des paramètres de facturation (PUT remplace toute la ligne
            // en base) — seul le catalogue change, tout le reste (adresse, SIRET, etc.) est préservé.
            const { logo_url, default_tva_rate, default_tva_custom_rate, tva_rates, enable_cover_count, ...safeSettings } = fullSettings;
            await moduleApi.saveBillingSettings({ ...safeSettings, prestations_catalog });
            setCatalogueModifie(false);
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e) {
            console.error('Failed to save prestations catalog', e);
        } finally {
            setSaving(false);
        }
    };

        const prestationsVisibles = visibleItems.filter((it) => it.kind !== 'option');
    const optionsVisibles = visibleItems.filter((it) => it.kind === 'option');

    const renderCarte = (item: PrestationCatalogItem) => {
                    const isExpanded = expandedId === item.id;
                    const objet = subcategories.find((sc) => sc.id === item.subcategory_id);
                    return (
                        <div
                            key={item.id}
                            className={`rounded-xl border bg-surface overflow-hidden flex flex-col ${isExpanded ? 'md:col-span-2 xl:col-span-3' : 'border-border'}`}
                            style={isExpanded ? { borderColor: 'var(--acc)' } : undefined}
                        >
                            <button
                                type="button"
                                onClick={() => setExpandedId(isExpanded ? null : item.id)}
                                className="w-full flex items-start justify-between gap-3 px-4 py-3.5 text-left hover:bg-surface-soft/60 transition-colors"
                            >
                                <div className="min-w-0 space-y-1.5">
                                    <span className="block font-bold text-content text-sm">{item.label || 'Sans nom'}</span>
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        {/* Pastilles aux couleurs d'état de la charte : l'objet en succès,
                                            l'option à l'accent, « sans objet » en neutre, « masquée » en
                                            attention. Hauteur fixe → nowrap + shrink-0 (passation §9.5). */}
                                        <span className="inline-flex items-center h-[22px] px-2 rounded-full bg-surface-soft border border-border text-[11px] font-bold text-content tabular-nums whitespace-nowrap shrink-0">
                                            {item.kind === 'option' && optionsEnPourcent.has(item.id) ? '% du prix de la prestation' : `${item.unit_price_ttc.toFixed(2)} € · ${UNIT_LABEL_COURT[item.unit_type]}`}
                                        </span>
                                        {item.kind === 'option' ? (
                                            <span className="inline-flex items-center h-[22px] px-2 rounded-full text-[10.5px] font-bold whitespace-nowrap shrink-0" style={{ background: 'var(--acc-bg)', color: 'var(--acc)' }}>
                                                {optionsEnPourcent.has(item.id) ? 'Option' : `Option · ${(item.option_item_ids || []).length || 'toutes'} visée${(item.option_item_ids || []).length > 1 ? 'es' : ''}`}
                                            </span>
                                        ) : objet ? (
                                            <span className="inline-flex items-center h-[22px] px-2 rounded-full text-[10.5px] font-bold whitespace-nowrap shrink-0" style={{ background: 'var(--st-ok-bg)', color: 'var(--st-ok-fg)' }}>{objet.name}</span>
                                        ) : (
                                            <span className="inline-flex items-center h-[22px] px-2 rounded-full bg-surface-soft text-[10.5px] font-bold text-content-muted whitespace-nowrap shrink-0">Sans objet</span>
                                        )}
                                        {item.simulateur_hidden && (
                                            <span className="inline-flex items-center h-[22px] px-2 rounded-full text-[10.5px] font-bold whitespace-nowrap shrink-0" style={{ background: 'var(--st-warn-bg)', color: 'var(--st-warn-fg)' }}>Masquée</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); duplicateItem(item.id); }}
                                        className="w-[30px] h-[30px] flex items-center justify-center rounded-lg text-content-muted hover:bg-surface-soft hover:text-content transition-colors shrink-0"
                                        aria-label="Dupliquer la prestation"
                                        title="Dupliquer (ex: pour la ranger aussi dans une autre catégorie)"
                                    >
                                        <Copy size={14} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); removeItem(item.id); }}
                                        className="w-[30px] h-[30px] flex items-center justify-center rounded-lg text-[#8f5450] hover:bg-[#fdf4f3] dark:hover:bg-[#2a1614] transition-colors shrink-0"
                                        aria-label="Supprimer la prestation"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                    <ChevronDown size={16} className={`text-content-muted transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                </div>
                            </button>
                            <AnimatePresence>
                                {isExpanded && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        className="overflow-hidden"
                                    >
                                        <div className="px-4 pb-4 pt-1 border-t border-border space-y-3">
                                            <Field label="Nom de la prestation">
                                                {({ id }) => (
                                                    <Input
                                                        id={id}
                                                        type="text"
                                                        value={item.label}
                                                        onChange={(e) => updateItem(item.id, { label: e.target.value })}
                                                    />
                                                )}
                                            </Field>
                                            <PrestationForm item={item} allowedTvaRates={allowedTvaRates} categories={categories} subcategories={subcategories} catalog={items} onChange={(patch) => updateItem(item.id, patch)} prixEnPourcent={item.kind === 'option' && optionsEnPourcent.has(item.id)} />
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    );
    };

    if (loading) {
    return (
            <div className="py-20 text-center text-content-muted flex flex-col items-center gap-2">
                <Loader2 size={28} className="animate-spin" />
                <span className="text-sm">Chargement du catalogue...</span>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            {/* En-tête (maquette 10a) : bande d'ambiance de 96 px comme le planning, puis la barre de
                contrôles. L'animation ne vit que dans la bande — rien ne bouge sous elle. */}
            <header className="bg-surface border border-border rounded-2xl overflow-hidden">
                <AmbianceBand className="h-[96px]">
                    <div className="h-[96px] px-[18px] py-[15px] flex flex-col justify-between">
                        <div className="flex items-center justify-between gap-3">
                            <span className="text-[10.5px] font-bold uppercase tracking-[.08em] text-content-muted truncate">{user?.company_name || ''}</span>
                            <span className="inline-flex items-center h-6 px-2.5 rounded-full bg-surface/70 border border-content/10 text-[10.5px] font-bold text-content-muted whitespace-nowrap shrink-0">{AMB[ambiance.active].label}</span>
                        </div>
                        <div className="text-[21px] font-bold tracking-[-0.02em] text-content leading-none">Prestations</div>
                    </div>
                </AmbianceBand>

                <div className="px-[18px] py-3.5 border-t border-border flex flex-wrap items-start justify-between gap-4">
                    <p className="text-[12.5px] text-content-muted min-w-0">Configurez les prestations que vous proposez : prix, TVA, durée estimée.</p>
                    {activeSection === 'catalogue' && (
                        <button
                            type="button"
                            onClick={() => setShowAddModal(true)}
                            className="h-9 px-4 inline-flex items-center gap-1.5 rounded-full text-[13px] font-bold text-white whitespace-nowrap shrink-0 transition-opacity hover:opacity-90"
                            style={{ background: 'var(--acc)' }}
                        >
                            <Plus size={15} /> Ajouter une prestation
                        </button>
                    )}
                </div>
            </header>

            {/* Deux rangées de bascules : la section, puis l'audience (patron « catalogue », passation §8). */}
            <div className="flex flex-wrap gap-3">
                <Segmented
                    ariaLabel="Section"
                    value={activeSection}
                    onChange={(v) => setActiveSection(v as 'catalogue' | 'formules' | 'tarifs')}
                    options={[{ value: 'catalogue', label: 'Catalogue' }, { value: 'formules', label: 'Formules' }, { value: 'tarifs', label: 'Réductions et options' }]}
                />
                <Segmented
                    ariaLabel="Public visé"
                    value={activeAudience === 'PRIVÉ' ? 'PRIVÉ' : 'PROFESSIONNEL'}
                    onChange={(v) => setActiveAudience(v as ClientTypeScope)}
                    options={[{ value: 'PROFESSIONNEL', label: 'Professionnels' }, { value: 'PRIVÉ', label: 'Particuliers' }]}
                />
            </div>

            {activeSection === 'tarifs' ? (
                <TarifsProSection audience={activeAudience === 'PRIVÉ' ? 'PRIVÉ' : 'PROFESSIONNEL'} />
            ) : activeSection === 'formules' ? (
                activeAudience === 'PRIVÉ'
                    ? <FormulesSection catalog={items} categories={categories} onCategoriesChanged={loadCategories} audience={activeAudience} />
                    : <FormulesProPage catalog={items} />
            ) : (
            <>

            {/* En cartes, comme les formules (demande d'Emeni, 2026-09-09) : une liste de lignes ne laissait
                rien voir de ce qui compte maintenant sur une prestation — son objet, sa nature, sa
                visibilite. La carte depliee prend toute la largeur, le formulaire d'edition etant trop
                dense pour une colonne. */}
            {visibleItems.length === 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    <div className="md:col-span-2 xl:col-span-3 text-center py-16 border border-dashed border-border rounded-2xl bg-surface/40">
                        <Package size={32} className="mx-auto mb-2 text-content-muted opacity-40" />
                        <p className="text-content-muted text-sm">{items.length === 0 ? "Aucune prestation configurée pour l'instant." : `Aucune prestation pour les ${activeAudience === 'PRIVÉ' ? 'particuliers' : 'professionnels'} pour l'instant.`}</p>
                    </div>
                </div>
            ) : (
            <>
            {/* Prestations et options séparées (demande d'Emeni, 2026-10-17) : on les mélangeait, on s'y perdait. */}
            <section aria-label="Prestations" className="space-y-3">
                <div className="flex items-baseline gap-2">
                    <h2 className="text-base font-bold text-content">Prestations</h2>
                    <span className="text-xs text-content-muted">{prestationsVisibles.length} · ce que le client fait nettoyer</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {prestationsVisibles.map(renderCarte)}
                </div>
            </section>
            {optionsVisibles.length > 0 && (
            <section aria-label="Options" className="space-y-3 pt-2">
                <div className="flex items-baseline gap-2">
                    <h2 className="text-base font-bold text-content">Options</h2>
                    <span className="text-xs text-content-muted">{optionsVisibles.length} · traitements en plus d'une prestation (désinfection, hydrofuge...)</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {optionsVisibles.map(renderCarte)}
                </div>
            </section>
            )}
            </>
            )}

            <details className="rounded-xl border border-border bg-surface-soft/40 group">
                <summary className="cursor-pointer select-none px-4 py-3 text-sm font-bold text-content-muted hover:text-content">
                    Organisation du catalogue <span className="font-normal text-xs">· catégories et parcours du simulateur</span>
                </summary>
                <div className="px-4 pb-4 space-y-4">
                <CategoryManager categories={categories} onChanged={loadCategories} />

                <SimulateurParcoursSettings
                    audience={activeAudience}
                    catalogueModifie={catalogueModifie}
                    subcategories={subcategories.filter((sc) => (sc.client_type_scope || 'TOUS') === 'TOUS' || sc.client_type_scope === activeAudience)}
                    categories={categories}
                    countByObjet={items.reduce((acc: Record<string, number>, it) => {
                        if (it.kind !== 'option' && it.subcategory_id) acc[it.subcategory_id] = (acc[it.subcategory_id] || 0) + 1;
                        return acc;
                    }, {})}
                    onChanged={loadSubcategories}
                />
                </div>
            </details>

            <div className="flex justify-end">
                {/* Bouton principal de la charte : pilule, fond encre — l'accent reste réservé à
                    « Ajouter », la seule pilule d'accent de l'écran. */}
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="h-[42px] px-6 inline-flex items-center gap-2 rounded-full bg-[#15161a] dark:bg-content text-white dark:text-[#0a0c10] text-[13px] font-bold whitespace-nowrap shrink-0 transition-opacity hover:opacity-90 disabled:opacity-50"
                >
                    <Save size={15} /> {saving ? 'Enregistrement…' : saved ? 'Enregistré ✓' : 'Enregistrer'}
                </button>
            </div>

            {showAddModal && (
                <AddPrestationModal
                    allowedTvaRates={allowedTvaRates}
                    categories={categories}
                    subcategories={subcategories}
                    catalog={items}
                    defaultScope={activeAudience}
                    onClose={() => setShowAddModal(false)}
                    onCreate={handleCreate}
                />
            )}
            </>
            )}
        </div>
    );
};
